/**
 * Atlas PM · 任务详情抽屉 · 数据 hooks
 *
 * 数据来源（全部为真实接口，已 curl 验证）：
 *   GET /api/tasks/:id                        任务详情
 *   GET /api/tasks/:id/progress               进度总览（含每指标完成度）
 *   GET /api/tasks/:taskId/metrics            指标 + 要素 + 填写值
 *   GET /api/tasks/:taskId/completeness       齐套率（P6.51：按「需附件要素」核算）
 *   GET /api/approvals?taskId=:id             该任务的审批实例
 *   GET /api/approvals/:id/timeline           审批时间线
 *   GET /api/audit/logs?resourceType=task&resourceId=:id   操作留痕（按类型 + id 精确过滤）
 *
 * ⭐ 进度是算出来的：本文件不提供任何「填进度」的写入口。
 *    要素勾选 → PUT /elements/:id/value → 失效相关 query → 进度自动刷新。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { useAuthStore } from '../../stores/auth';
import { taskStatusMeta } from '@atlaspm/shared';
import type { Paginated } from '../../api/types';
import type {
  ApprovalInstanceInfo,
  ApprovalTimelineItem,
  CompletenessInfo,
  MetricInfo,
  TaskDetail,
  TaskProgressInfo,
  TaskStatusMeta,
} from '@atlaspm/shared';

/* ═══════════════════════════════════════════════════════════
   状态视觉元数据 —— ⚠️ P6.46 起改为引用 @atlaspm/shared 单一数据源
   （本文件不再维护映射表，仅保留 stateMeta 作为向后兼容薄封装）
   ═══════════════════════════════════════════════════════════ */

export type StateMeta = TaskStatusMeta;

export function stateMeta(state: string | undefined): StateMeta {
  return taskStatusMeta(state);
}

export const isFrozenState = (state: string | undefined): boolean =>
  state === 'FROZEN' || state === 'ARCHIVED';

export const isUnlockedEditing = (state: string | undefined): boolean =>
  state === 'UNLOCKED_EDITING';

export const isLockedState = (state: string | undefined): boolean =>
  isFrozenState(state) || state === 'SUBMITTED' || state === 'APPROVING';

/* ═══════════════════════════════════════════════════════════
   读：组合查询
   ═══════════════════════════════════════════════════════════ */

export function useTaskDetail(taskId: string) {
  const enabled = Boolean(taskId);
  const task = useQuery({
    queryKey: qk.task(taskId),
    queryFn: () => api.get<TaskDetail>(`/tasks/${taskId}`),
    enabled,
  });
  const progress = useQuery({
    queryKey: qk.taskProgress(taskId),
    queryFn: () => api.get<TaskProgressInfo>(`/tasks/${taskId}/progress`),
    enabled,
  });
  const metrics = useQuery({
    queryKey: qk.metrics(taskId),
    queryFn: () => api.get<MetricInfo[]>(`/tasks/${taskId}/metrics`),
    enabled,
  });
  const completeness = useQuery({
    queryKey: qk.completeness(taskId),
    queryFn: () => api.get<CompletenessInfo>(`/tasks/${taskId}/completeness`),
    enabled,
  });

  return {
    task,
    progress,
    metrics,
    completeness,
    isLoading:
      task.isLoading || progress.isLoading || metrics.isLoading || completeness.isLoading,
    isError: task.isError,
    error: (task.error as Error | null) ?? null,
  };
}

/** 任务的审批实例（取最新一条；后端按 createdAt desc 排序） */
export function useTaskApproval(taskId: string, enabled = true) {
  return useQuery({
    queryKey: qk.approvals({ taskId }),
    queryFn: () =>
      api.get<Paginated<ApprovalInstanceInfo>>(
        `/approvals${qs({ taskId, pageSize: 5 })}`,
      ),
    enabled: Boolean(taskId) && enabled,
    select: (page) => page.items[0] ?? null,
  });
}

/** 审批时间线 */
export function useApprovalTimeline(instanceId: string | null | undefined) {
  return useQuery({
    queryKey: [...qk.approval(instanceId ?? ''), 'timeline'] as const,
    queryFn: () =>
      api.get<{ items: ApprovalTimelineItem[] }>(`/approvals/${instanceId}/timeline`),
    enabled: Boolean(instanceId),
    select: (r) => r.items,
  });
}

/**
 * 任务操作留痕。
 *
 * ⚠️ P6.18：必须同时传 resourceType + resourceId。
 *    仅传 resourceId 会在 task.id / project.id / element.id 撞车时串数据
 *    （例如 task 97 会混入 project 97 的 PROJECT_CREATE/DELETE）。
 *    后端查询已改为大小写不敏感，故历史大写 'TASK' 记录也能命中。
 */
export function useTaskAudit(taskId: string, enabled = true) {
  return useQuery({
    queryKey: [...qk.auditLogs({ resourceType: 'task', resourceId: taskId }), 'detail'] as const,
    queryFn: () =>
      api.get<{ items: AuditLogItem[]; total: number }>(
        `/audit/logs${qs({ resourceType: 'task', resourceId: taskId, limit: 50 })}`,
      ),
    enabled: Boolean(taskId) && enabled,
    select: (r) => r.items,
  });
}

/** 操作条目（以后端返回字段为准，字段名为 snake_case） */
export interface AuditLogItem {
  id: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_role: string | null;
  action: string;
  resource_type: string;
  resource_id: string;
  project_id: string | null;
  before_value: unknown;
  after_value: unknown;
  diff: Record<string, { from?: unknown; to?: unknown }> | null;
  reason: string | null;
  mode: string;
  ip: string | null;
  trace_id: string | null;
  prev_hash: string | null;
  row_hash: string | null;
  created_at: string;
}

/** 冻结快照（GET /freeze/tasks/:id/snapshots） */
export interface FreezeSnapshotItem {
  id: string;
  taskId: string;
  freezeVersion: number;
  snapshot: unknown;
  snapshotHash: string;
  prevHash: string | null;
  frozenBy: string | null;
  frozenAt: string;
}

export function useFreezeSnapshots(taskId: string, enabled = true) {
  return useQuery({
    queryKey: ['freeze', 'snapshots', taskId] as const,
    queryFn: () =>
      api.get<{ items: FreezeSnapshotItem[] }>(`/freeze/tasks/${taskId}/snapshots`),
    enabled: Boolean(taskId) && enabled,
    select: (r) => r.items,
  });
}

/* ═══════════════════════════════════════════════════════════
   写：要素填值（触发进度重算）
   ═══════════════════════════════════════════════════════════ */

export interface SetElementValueArgs {
  elementId: string;
  /** 补充值（可选；只改完成度/质量时可不传） */
  value?: unknown;
  isComplete?: boolean;
  /** 完成度 0-100（用户填） */
  completionPct?: number;
  /** 完成质量 0-100（用户填） */
  qualityPct?: number;
  /** 乐观锁：传上次拿到的 version */
  version?: number;
}

export function useSetElementValue(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: SetElementValueArgs) =>
      api.put(`/elements/${args.elementId}/value`, {
        value: args.value,
        isComplete: args.isComplete,
        completionPct: args.completionPct,
        qualityPct: args.qualityPct,
        version: args.version,
      }),
    onSettled: () => {
      // ⭐ 进度自动刷新：失效进度 / 任务 / 指标三条 query
      queryClient.invalidateQueries({ queryKey: qk.taskProgress(taskId) });
      queryClient.invalidateQueries({ queryKey: qk.task(taskId) });
      queryClient.invalidateQueries({ queryKey: qk.metrics(taskId) });
      queryClient.invalidateQueries({ queryKey: qk.tasks() });
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}

/* ═══════════════════════════════════════════════════════════
   写：要素附件（P6.41，复用 attachment 表 ownerType=ELEMENT）
   ═══════════════════════════════════════════════════════════ */

export interface AddElementAttachmentArgs {
  elementId: string;
  fileId: string;
  caption?: string;
}

export function useAddElementAttachment(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ elementId, fileId, caption }: AddElementAttachmentArgs) =>
      api.post(`/elements/${elementId}/attachments`, { fileId, caption }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: qk.metrics(taskId) });
      queryClient.invalidateQueries({ queryKey: qk.taskProgress(taskId) });
      // ⭐ P6.54：附件变化影响齐套率 → 失效任务级齐套率 + 项目级聚合（含全国态势 KPI）
      queryClient.invalidateQueries({ queryKey: qk.completeness(taskId) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteElementAttachment(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => api.delete(`/attachments/${attachmentId}`),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: qk.metrics(taskId) });
      queryClient.invalidateQueries({ queryKey: qk.taskProgress(taskId) });
      // ⭐ P6.54：附件删除影响齐套率 → 失效任务级 + 项目级聚合
      queryClient.invalidateQueries({ queryKey: qk.completeness(taskId) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/* ═══════════════════════════════════════════════════════════
   写：发起审批 / 申请解锁
   ═══════════════════════════════════════════════════════════ */

export interface StartApprovalArgs {
  submitRemark?: string;
}

/**
 * ⭐ P8.17：提交与发起审批拆成【两个独立的人工动作】，不再自动连发。
 *
 *   ① 提交        POST /tasks/:id/submit    仅此一步 → 任务进入 SUBMITTED
 *   ② 发起审批    POST /tasks/:id/approval  仅此一步 → 任务进入 APPROVING
 *
 * 设计意图（用户明确要求）：
 *   - 「发起审批」必须由人再点一次，不能作为「提交」的副作用静默发生；
 *   - 中间「已提交」是等待期，可「↩ 撤回提交」退回草稿，也可「发起审批」前进；
 *   - 因为两步之间不再 invalidate，UI 只会在各自成功后各重绘一次，不会闪现。
 *
 * 注意：后端 /approval 仍要求任务已是 SUBMITTED（approval.service.ts），
 * 故必须先完成①，再允许② —— 这由「已提交」状态下的按钮切换保证。
 */
export function useSubmitTask(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<TaskDetail>(`/tasks/${taskId}/submit`),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: qk.task(taskId) });
      queryClient.invalidateQueries({ queryKey: qk.tasks() });
      queryClient.invalidateQueries({ queryKey: ['audit', 'logs'] });
    },
  });
}

/**
 * ⭐ P8.17：发起审批 —— 只调用 POST /tasks/:id/approval，绝不隐式先 /submit。
 *
 * 前提由 UI 保证：仅任务处于 SUBMITTED 时展示该动作（见 useEditPolicy.canStartApproval）。
 */
export function useStartApproval(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args?: StartApprovalArgs) => {
      const submitRemark = args?.submitRemark;
      return api.post(`/tasks/${taskId}/approval`, submitRemark ? { submitRemark } : {});
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: qk.task(taskId) });
      queryClient.invalidateQueries({ queryKey: qk.tasks() });
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}

/**
 * ⭐ P6.23 撤回提交：SUBMITTED → DRAFT。
 *
 * 场景：任务提交后（尤其审批发起因配置问题被 P6.22 拦截）卡在 SUBMITTED，
 * 用户既改不了也删不掉。撤回后退回草稿，可继续编辑并重新提交。
 */
export function useWithdraw(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<TaskDetail>(`/tasks/${taskId}/withdraw`),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: qk.task(taskId) });
      queryClient.invalidateQueries({ queryKey: qk.tasks() });
      // 撤回后任务重新可编辑，进度 / 指标缓存一并刷新
      queryClient.invalidateQueries({ queryKey: qk.taskProgress(taskId) });
      queryClient.invalidateQueries({ queryKey: qk.metrics(taskId) });
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      queryClient.invalidateQueries({ queryKey: ['audit', 'logs'] });
    },
  });
}

/**
 * ⭐ P7.6 删除任务（软删除）。
 *
 * 前端仅对未提交状态（DRAFT / IN_PROGRESS / REJECTED / CANCELLED）提供入口；
 * 后端 TaskService.remove() 做创建人/负责人/超管鉴权兜底，并重算项目聚合。
 *
 * 删除成功后失效任务、项目（地图 KPI / 星图首任务）、审批与审计缓存，
 * 使当前视图（地图 / 星图 / 任务列表）自动刷新。
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      api.delete<{ ok: boolean; id: string }>(`/tasks/${taskId}`),
    onSettled: () => {
      // ⚠️ 用「基键」前缀失效：['tasks'] 覆盖列表({all}/{projectId})、树、依赖图、详情；
      //    ['projects'] 覆盖地图光点/聚合 KPI 与星图首任务。
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      queryClient.invalidateQueries({ queryKey: ['audit', 'logs'] });
    },
  });
}

export interface UnlockRequestResult {
  id: string;
  taskId: string;
  applicantId: string;
  reason: string;
  status: string;
  createdAt: string;
}

export function useRequestUnlock(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<UnlockRequestResult>(`/freeze/tasks/${taskId}/unlock-requests`, { reason }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: qk.task(taskId) });
      queryClient.invalidateQueries({ queryKey: ['freeze'] });
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}

/* ═══════════════════════════════════════════════════════════
   编辑权限判定（前端感知，后端四层防线兜底）
   ═══════════════════════════════════════════════════════════ */

export interface EditPolicy {
  state: string;
  /** 完全冻结（FROZEN / ARCHIVED） */
  isFrozen: boolean;
  /** 解冻编辑窗口内（UNLOCKED_EDITING） */
  isUnlocked: boolean;
  /** 审批中 / 已提交（临时锁定） */
  isApprovalLocked: boolean;
  isLocked: boolean;
  /** 是否管理员（拥有解冻编辑权） */
  isAdmin: boolean;
  /** 是否可申请解锁 */
  canRequestUnlock: boolean;
  /**
   * ⭐ P7.6：是否可删除该任务。
   *
   * 显示条件（与后端 TaskService.remove() 鉴权一致）：
   *   ① 当前用户是任务的创建人（createdBy）或负责人（ownerId），
   *      或拥有 `task:delete` 权限（超管恒真）；
   *   ② 状态 ∈ { DRAFT, IN_PROGRESS, REJECTED, CANCELLED }（未提交状态）。
   *
   * ⚠️ SUBMITTED / APPROVING / FROZEN / ARCHIVED / UNLOCKED_EDITING 不显示删除入口。
   *    前端隐藏只是体验，后端仍会二次鉴权兜底。
   */
  canDeleteTask: boolean;
  /**
   * ⭐ P7.5：是否可编辑任务本身（基本信息 + 指标与要素）。
   *
   * 仅在**尚未提交审批**的可写状态下开放：DRAFT / IN_PROGRESS / REJECTED，
   * 且需 task:update 权限。
   *
   * ⚠️ 以下状态【不】显示编辑入口：
   *   SUBMITTED（已提交）/ APPROVING（审批中）/ FROZEN / ARCHIVED /
   *   UNLOCKED_EDITING / CANCELLED —— 这些状态需先「撤回提交」或走「申请解冻」。
   *   撤回提交后状态回到 DRAFT，编辑入口自动重新出现。
   */
  canEditTask: boolean;
  canEditElements: boolean;
  canSubmitDeliverables: boolean;
  /**
   * ⭐ P8.17：是否可「提交」（第一个独立动作，POST /tasks/:id/submit）。
   * 仅未提交状态：DRAFT / IN_PROGRESS / REJECTED，且需 task:submit 权限。
   */
  canSubmitTask: boolean;
  /**
   * ⭐ P8.17：是否可「发起审批」（第二个独立动作，POST /tasks/:id/approval）。
   * 仅等待期 SUBMITTED，且需 task:submit 权限（可撤回、可发起）。
   */
  canStartApproval: boolean;
  /**
   * 兼容保留：提交或发起审批任一可用时为真（= canSubmitTask || canStartApproval）。
   * 目前仅 TaskDrawer 使用；新的调用方请按动作分别使用上面两个字段。
   */
  canSubmitApproval: boolean;
  canApprove: boolean;
  /** ⭐ P6.23：SUBMITTED 可撤回提交（退回草稿） */
  canWithdraw: boolean;
  /** 是否应展示「管理员特权」提示 */
  showAdminPrivilege: boolean;
}

export function useEditPolicy(task: TaskDetail | undefined): EditPolicy {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const userId = useAuthStore((s) => s.user?.userId ?? null);
  const state = task?.state ?? 'DRAFT';

  const isFrozen = isFrozenState(state);
  const isUnlocked = isUnlockedEditing(state);
  const isApprovalLocked = state === 'SUBMITTED' || state === 'APPROVING';
  const isLocked = isFrozen || isApprovalLocked;

  const isAdmin = hasPermission('freeze:unlocked_edit');

  // 冻结态（含审批中）对所有人都不可写；仅解冻编辑窗口内管理员可写
  const writableNow = !isLocked || isUnlocked;

  // ⭐ P7.6：删除仅限未提交状态，且需为创建人 / 负责人 / 有 task:delete 权限者
  const deletableState =
    state === 'DRAFT' ||
    state === 'IN_PROGRESS' ||
    state === 'REJECTED' ||
    state === 'CANCELLED';
  const isCreator = Boolean(task && userId && task.createdBy === userId);
  const isOwner = Boolean(task && userId && task.ownerId === userId);

  // ⭐ P8.17：两个人工动作拆开判定，footer 依状态只显示一个主按钮
  const canSubmitTask =
    hasPermission('task:submit') &&
    (state === 'DRAFT' || state === 'IN_PROGRESS' || state === 'REJECTED');
  const canStartApproval = hasPermission('task:submit') && state === 'SUBMITTED';

  return {
    state,
    isFrozen,
    isUnlocked,
    isApprovalLocked,
    isLocked,
    isAdmin,
    canRequestUnlock:
      hasPermission('freeze:unlock_request') && (isFrozen || isUnlocked),
    canDeleteTask:
      Boolean(task) &&
      deletableState &&
      (hasPermission('task:delete') || isCreator || isOwner),
    canEditTask:
      hasPermission('task:update') &&
      (state === 'DRAFT' || state === 'IN_PROGRESS' || state === 'REJECTED'),
    canEditElements: hasPermission('element:fill') && writableNow,
    canSubmitDeliverables: hasPermission('deliverable:submit') && writableNow,
    canSubmitTask,
    canStartApproval,
    canSubmitApproval: canSubmitTask || canStartApproval,
    canApprove: hasPermission('approval:approve') && state === 'APPROVING',
    // ⭐ P6.23：仅 SUBMITTED 可撤回；APPROVING 已进入审批，不可撤回
    canWithdraw: hasPermission('task:update') && state === 'SUBMITTED',
    showAdminPrivilege: isAdmin && (isFrozen || isUnlocked),
  };
}
